import React, { useEffect, useMemo, useState } from 'react';
import {
  App,
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
} from 'antd';
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  CopyOutlined,
  LinkOutlined,
  PrinterOutlined,
  SaveOutlined,
  SendOutlined,
  StopOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import customerSatisfactionSurveyService from '@/services/customerSatisfactionSurvey.service';
import salesOrderService, { type SalesOrder } from '@/services/salesOrder.service';
import { useActiveCustomers } from '@/hooks/useCustomers';
import {
  CSS_FORM_CODE,
  CSS_PARAMETER_KEYS,
  CSS_PARAMETER_LABELS,
  CSS_SCORE_OPTIONS,
  type CssParameterKey,
  type CustomerSatisfactionSurvey,
} from '@/constants/customer-satisfaction-survey';

const { Title, Text, Paragraph } = Typography;

const STATUS_COLOR: Record<string, string> = {
  Draft: 'default',
  Sent: 'processing',
  Completed: 'success',
  Reviewed: 'cyan',
  Cancelled: 'error',
};

function buildProjectPoRef(so: SalesOrder): string {
  const parts = [so.customer_po_number, so.project_name, so.sales_order_number]
    .map((x) => String(x ?? '').trim())
    .filter(Boolean);
  // Prefer PO + project; always keep SO number as fallback context
  if (so.customer_po_number && so.project_name) {
    return `${so.customer_po_number} / ${so.project_name}`;
  }
  if (so.customer_po_number) return so.customer_po_number;
  if (so.project_name) return so.project_name;
  return so.sales_order_number || parts[0] || '';
}

function buildScopeOfWork(so: SalesOrder): string {
  return (
    String(so.commercial_scope_text || '').trim() ||
    String(so.customer_requirements_text || '').trim() ||
    String(so.customer_notes || '').trim() ||
    String(so.notes || '').trim() ||
    ''
  );
}

function buildAddressFromSo(so: SalesOrder): string {
  return (
    String(so.customer_address || '').trim() ||
    String(so.billing_address || '').trim() ||
    String(so.delivery_address || '').trim() ||
    ''
  );
}

const CustomerSatisfactionDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isNew = id === 'new';
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const selectedCustomerId = Form.useWatch('customer_id', form);

  const { data: customers = [] } = useActiveCustomers();
  const customerOptions = useMemo(
    () =>
      (customers as Array<{ id: string; customer_name?: string; name?: string }>).map((c) => ({
        value: c.id,
        label: c.customer_name || c.name || c.id,
      })),
    [customers]
  );

  const { data: salesOrders = [], isFetching: loadingOrders } = useQuery({
    queryKey: ['customer-satisfaction-sales-orders', selectedCustomerId],
    queryFn: () => salesOrderService.getAll({ customer_id: selectedCustomerId }),
    enabled: Boolean(selectedCustomerId),
  });

  const salesOrderOptions = useMemo(
    () =>
      (salesOrders as SalesOrder[]).map((so) => ({
        value: so.id,
        label: [
          so.sales_order_number,
          so.customer_po_number ? `PO ${so.customer_po_number}` : null,
          so.project_name || null,
          so.order_status ? `(${so.order_status})` : null,
        ]
          .filter(Boolean)
          .join(' — '),
      })),
    [salesOrders]
  );

  const applySalesOrder = (soId: string | undefined) => {
    if (!soId) {
      form.setFieldsValue({ sales_order_id: undefined });
      return;
    }
    const so = (salesOrders as SalesOrder[]).find((x) => x.id === soId);
    if (!so) return;
    const addressFromSo = buildAddressFromSo(so);
    form.setFieldsValue({
      sales_order_id: so.id,
      project_or_po_reference: buildProjectPoRef(so),
      scope_of_work: buildScopeOfWork(so),
      ...(addressFromSo ? { customer_address: addressFromSo } : {}),
      ...(so.customer_name ? { customer_name_snapshot: so.customer_name } : {}),
    });
  };

  const { data: survey, isLoading } = useQuery({
    queryKey: ['customer-satisfaction', id],
    queryFn: () => customerSatisfactionSurveyService.getById(id!),
    enabled: !isNew && Boolean(id),
  });

  const { data: kpiData } = useQuery({
    queryKey: ['customer-satisfaction-kpi'],
    queryFn: () => customerSatisfactionSurveyService.getKpi(),
  });

  useEffect(() => {
    if (!survey) return;
    form.setFieldsValue({
      customer_id: survey.customer_id,
      sales_order_id: survey.sales_order_id ?? undefined,
      customer_name_snapshot: survey.customer_name_snapshot,
      customer_address: survey.customer_address,
      scope_of_work: survey.scope_of_work,
      project_or_po_reference: survey.project_or_po_reference,
      feedback_date: survey.feedback_date ? dayjs(survey.feedback_date) : null,
      sales_notes: survey.sales_notes,
      kpi_target_pct: survey.kpi_target_pct ?? kpiData?.kpi_target_pct,
    });
    if (survey.share_path) {
      setShareUrl(`${window.location.origin}${survey.share_path}`);
    }
  }, [survey, form, kpiData]);

  const createMut = useMutation({
    mutationFn: (body: Record<string, unknown>) => customerSatisfactionSurveyService.create(body),
    onSuccess: (row) => {
      message.success('Survey created');
      queryClient.invalidateQueries({ queryKey: ['customer-satisfaction'] });
      if (row?.id) navigate(`/growth/customer-satisfaction/${row.id}`, { replace: true });
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Create failed'),
  });

  const updateMut = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      customerSatisfactionSurveyService.update(id!, body),
    onSuccess: () => {
      message.success('Saved');
      queryClient.invalidateQueries({ queryKey: ['customer-satisfaction', id] });
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Save failed'),
  });

  const sendMut = useMutation({
    mutationFn: () => customerSatisfactionSurveyService.send(id!),
    onSuccess: (row) => {
      message.success('Share link ready');
      if (row?.share_path) setShareUrl(`${window.location.origin}${row.share_path}`);
      queryClient.invalidateQueries({ queryKey: ['customer-satisfaction', id] });
      queryClient.invalidateQueries({ queryKey: ['customer-satisfaction'] });
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Send failed'),
  });

  const reviewMut = useMutation({
    mutationFn: (body: { sales_notes?: string; kpi_target_pct?: number }) =>
      customerSatisfactionSurveyService.review(id!, body),
    onSuccess: (row) => {
      const result = row?.satisfaction_result;
      message.success(
        result === 'Unsatisfied'
          ? 'Reviewed — Unsatisfied (NCR required per procedure)'
          : 'Reviewed — Satisfied'
      );
      queryClient.invalidateQueries({ queryKey: ['customer-satisfaction', id] });
      queryClient.invalidateQueries({ queryKey: ['customer-satisfaction'] });
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Review failed'),
  });

  const cancelMut = useMutation({
    mutationFn: () => customerSatisfactionSurveyService.cancel(id!),
    onSuccess: () => {
      message.success('Survey cancelled');
      queryClient.invalidateQueries({ queryKey: ['customer-satisfaction', id] });
      queryClient.invalidateQueries({ queryKey: ['customer-satisfaction'] });
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Cancel failed'),
  });

  const onSave = async () => {
    const values = await form.validateFields();
    const body = {
      customer_id: values.customer_id,
      sales_order_id: values.sales_order_id || null,
      customer_name_snapshot: values.customer_name_snapshot,
      customer_address: values.customer_address,
      scope_of_work: values.scope_of_work,
      project_or_po_reference: values.project_or_po_reference,
      feedback_date: values.feedback_date?.format?.('YYYY-MM-DD') ?? null,
      sales_notes: values.sales_notes,
    };
    if (isNew) createMut.mutate(body);
    else updateMut.mutate(body);
  };

  const status = survey?.status || 'Draft';
  const editable = isNew || status === 'Draft';
  const canSend = !isNew && ['Draft', 'Sent', 'Pending'].includes(status);
  const canReview = !isNew && ['Completed', 'Reviewed'].includes(status);
  const canCancel = !isNew && !['Completed', 'Reviewed', 'Cancelled'].includes(status);

  const scoreRows = useMemo(() => {
    const scores = survey?.parameter_scores;
    return CSS_PARAMETER_KEYS.map((key) => ({
      key,
      label: CSS_PARAMETER_LABELS[key as CssParameterKey],
      score: scores?.[key as CssParameterKey]?.score ?? null,
      comments: scores?.[key as CssParameterKey]?.comments ?? null,
    }));
  }, [survey]);

  if (!isNew && isLoading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin />
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Space>
              <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/growth/customer-satisfaction')}>
                Register
              </Button>
              <Title level={3} style={{ margin: 0 }}>
                {isNew ? 'New Customer Satisfaction Survey' : survey?.survey_number || 'Survey'}
              </Title>
              {!isNew && <Tag color={STATUS_COLOR[status] || 'default'}>{status}</Tag>}
            </Space>
            <div>
              <Text type="secondary">{CSS_FORM_CODE}</Text>
            </div>
          </Col>
          <Col>
            <Space wrap>
              {!isNew && id && (
                <Button
                  icon={<PrinterOutlined />}
                  onClick={() => navigate(`/print/growth/customer-satisfaction/${id}`)}
                >
                  Print record
                </Button>
              )}
              {editable && (
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  loading={createMut.isPending || updateMut.isPending}
                  onClick={onSave}
                >
                  {isNew ? 'Create' : 'Save'}
                </Button>
              )}
              {canSend && (
                <Button
                  icon={<SendOutlined />}
                  loading={sendMut.isPending}
                  onClick={() => sendMut.mutate()}
                >
                  Generate / refresh link
                </Button>
              )}
              {canReview && (
                <Button
                  type="primary"
                  icon={<CheckCircleOutlined />}
                  loading={reviewMut.isPending}
                  onClick={() => {
                    const notes = form.getFieldValue('sales_notes');
                    const kpi = form.getFieldValue('kpi_target_pct');
                    reviewMut.mutate({
                      sales_notes: notes,
                      kpi_target_pct: kpi != null ? Number(kpi) : undefined,
                    });
                  }}
                >
                  Review KPI result
                </Button>
              )}
              {canCancel && (
                <Button danger icon={<StopOutlined />} loading={cancelMut.isPending} onClick={() => cancelMut.mutate()}>
                  Cancel
                </Button>
              )}
            </Space>
          </Col>
        </Row>

        {shareUrl && (
          <Card size="small">
            <Space direction="vertical" style={{ width: '100%' }}>
              <Text strong>
                <LinkOutlined /> Client survey link
              </Text>
              <Input.TextArea value={shareUrl} autoSize readOnly />
              <Button
                icon={<CopyOutlined />}
                onClick={() => {
                  void navigator.clipboard.writeText(shareUrl);
                  message.success('Link copied');
                }}
              >
                Copy link
              </Button>
              <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                Share this link with the customer to collect NTES-QSF-SAL-01 feedback.
              </Paragraph>
            </Space>
          </Card>
        )}

        <Card title="Survey header">
          <Form
            form={form}
            layout="vertical"
            initialValues={{
              feedback_date: dayjs(),
              kpi_target_pct: kpiData?.kpi_target_pct ?? 80,
            }}
          >
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="customer_id"
                  label="Customer"
                  rules={[{ required: true, message: 'Customer is required' }]}
                >
                  <Select
                    showSearch
                    optionFilterProp="label"
                    options={customerOptions}
                    disabled={!editable}
                    onChange={(cid) => {
                      const c = (
                        customers as Array<{
                          id: string;
                          customer_name?: string;
                          name?: string;
                          billing_address_line1?: string | null;
                          billing_address_line2?: string | null;
                          billing_city?: string | null;
                          billing_country?: string | null;
                        }>
                      ).find((x) => x.id === cid);
                      form.setFieldsValue({
                        sales_order_id: undefined,
                        project_or_po_reference: undefined,
                        scope_of_work: undefined,
                        customer_name_snapshot: c?.customer_name || c?.name || undefined,
                        customer_address: c
                          ? [
                              c.billing_address_line1,
                              c.billing_address_line2,
                              c.billing_city,
                              c.billing_country,
                            ]
                              .map((x) => String(x ?? '').trim())
                              .filter(Boolean)
                              .join(', ')
                          : undefined,
                      });
                    }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="customer_name_snapshot" label="Customer name (on form)">
                  <Input disabled={!editable} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="sales_order_id"
                  label="Customer order"
                  rules={[{ required: true, message: 'Select a customer order' }]}
                >
                  <Select
                    showSearch
                    allowClear
                    optionFilterProp="label"
                    placeholder={
                      selectedCustomerId
                        ? loadingOrders
                          ? 'Loading customer orders…'
                          : 'Select customer order'
                        : 'Select customer first'
                    }
                    options={salesOrderOptions}
                    disabled={!editable || !selectedCustomerId}
                    loading={loadingOrders}
                    onChange={(soId) => applySalesOrder(soId)}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="feedback_date" label="Feedback date">
                  <DatePicker style={{ width: '100%' }} disabled={!editable} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="project_or_po_reference" label="Project / PO reference">
                  <Input disabled={!editable} placeholder="Filled from customer order (editable)" />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item name="customer_address" label="Address">
                  <Input.TextArea rows={2} disabled={!editable} />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item name="scope_of_work" label="Scope of work">
                  <Input.TextArea rows={2} disabled={!editable} placeholder="Filled from customer order scope" />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item name="sales_notes" label="Sales notes (internal)">
                  <Input.TextArea rows={2} disabled={status === 'Cancelled'} />
                </Form.Item>
              </Col>
              {(canReview || survey?.satisfaction_pct != null) && (
                <Col xs={24} md={8}>
                  <Form.Item name="kpi_target_pct" label="KPI target % (Quality objectives)">
                    <InputNumber min={1} max={100} style={{ width: '100%' }} disabled={!canReview} />
                  </Form.Item>
                </Col>
              )}
            </Row>
          </Form>
        </Card>

        {!isNew && survey && (
          <Card title="Scores & KPI (NTES-QSF-SAL-01)">
            <Descriptions bordered size="small" column={{ xs: 1, md: 3 }} style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Total score (A)">
                {survey.total_score_a != null ? survey.total_score_a : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Max score (B)">{survey.max_score_b ?? 30}</Descriptions.Item>
              <Descriptions.Item label="Satisfaction %">
                {survey.satisfaction_pct != null ? `${Number(survey.satisfaction_pct).toFixed(2)}%` : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="KPI target %">{survey.kpi_target_pct ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="Result">
                {survey.satisfaction_result ? (
                  <Tag color={survey.satisfaction_result === 'Satisfied' ? 'success' : 'error'}>
                    {survey.satisfaction_result}
                  </Tag>
                ) : (
                  '—'
                )}
              </Descriptions.Item>
              <Descriptions.Item label="NCR required">
                {survey.requires_ncr ? <Tag color="error">Yes</Tag> : 'No'}
              </Descriptions.Item>
              <Descriptions.Item label="Client rep">{survey.client_rep_name || '—'}</Descriptions.Item>
              <Descriptions.Item label="Client email">{survey.client_rep_email || '—'}</Descriptions.Item>
              <Descriptions.Item label="Client date">
                {survey.client_rep_date ? dayjs(survey.client_rep_date).format('DD MMM YYYY') : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Overall comments" span={3}>
                {survey.overall_comments || '—'}
              </Descriptions.Item>
            </Descriptions>

            <Table
              size="small"
              pagination={false}
              rowKey="key"
              dataSource={scoreRows}
              columns={[
                { title: 'Parameter', dataIndex: 'label' },
                {
                  title: 'Score',
                  dataIndex: 'score',
                  width: 200,
                  render: (v: number | null) => {
                    if (v == null) return '—';
                    const opt = CSS_SCORE_OPTIONS.find((o) => o.value === v);
                    return opt?.label || String(v);
                  },
                },
                { title: 'Comments', dataIndex: 'comments', render: (v) => v || '—' },
              ]}
            />
            {survey.requires_ncr && (
              <Paragraph type="danger" style={{ marginTop: 16, marginBottom: 0 }}>
                Per NTES-QSP-SAL-001 §6.2: Unsatisfied outcome requires Non-Conformance Report and CAPA.
              </Paragraph>
            )}
          </Card>
        )}
      </Space>
    </div>
  );
};

export default CustomerSatisfactionDetailPage;
